import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle, useRef } from "react";
import { Table, Tabs, Popover, Input, Tooltip } from "antd";
import "./buildingtable.css";
import { RegisterIdContext } from "../../hooks/RegisterIdContext";
import apiUrl from "../../ApiEndpoint/config";
import apiClient from "../../ApiEndpoint/apiClient";
import { debounceTime, switchMap, catchError, tap } from 'rxjs/operators';
import { from, of, Subject } from 'rxjs';
import { toast } from 'react-toastify';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;


const BuildingTable = forwardRef((props, ref) => {
    const [buildingData, setBuildingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 100; // Show 100 records per page
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [tableKey, setTableKey] = useState(0);
    const [hasDownloaded, setHasDownloaded] = useState(true);
    const [acccountName, setAcccountName] = useState(null);
    const [oppName, setOppName] = useState(null);
    const [oppDesc, setOppDesc] = useState(null);
    const [contactName, setContactName] = useState(null);
    const [opportunityData, setOpportunityData] = useState([]);
    const [opportunityLoading, setOpportunityLoading] = useState(true);
    const [opportunityPage, setOpportunityPage] = useState(1);
    const [opportunityTotalCount, setOpportunityTotalCount] = useState(0); // Set a default or update it from API
    const opportunityPageSize = 1000;
    const [opsortColumn, setSortColumnOp] = useState(null);
    const [opsortOrder, setSortOrderOp] = useState(null);
    const searchSubjectRef = useRef(new Subject());
    const { bldType, bldOwner, bldMasterId, bldScore, state, city, floorArea, EUI, setBuildingNameContext } = useContext(RegisterIdContext);


    // Table Columns
    const buildingColumns = [
        { title: "Building Owner", dataIndex: "BUILDING_OWNER", key: "BUILDING_OWNER", sortColumn: "ownerofrecord", sorter: true },
        {
            title: "Building Name", dataIndex: "BUILDING_NAME", key: "BUILDING_NAME", sortColumn: "Master_Building_Name", sorter: true,
            render: (text, record) => (
                <span
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                        setBuildingNameContext(record.fullObject.master_id)
                    }}
                >
                    {text}
                </span>
            ),
        },
        { title: "Building type", dataIndex: "BUILDING_TYPE", key: "BUILDING_TYPE", sortColumn: "Master_Building_Type", sorter: true },
        { title: "Area (sqft)", dataIndex: "AREA", key: "AREA", sortColumn: "floor_area", sorter: true },
        { title: "EUI (kBtu/sqft)", dataIndex: "EUI", key: "EUI", sortColumn: "Site_EUI_kBtu_per_sqft", sorter: true },
        { title: "State", dataIndex: "STATE_NAME", key: "STATE_NAME", sortColumn: "State_Name", sorter: true },
        { title: "City", dataIndex: "CITY", key: "CITY", sortColumn: "city", sorter: true },
        { title: "Year of Completion", dataIndex: "YEAR", key: "YEAR", sortColumn: "year_built", sorter: true },
        { title: "Energy Star Score", dataIndex: "ENERGY", key: "ENERGY", sortColumn: "ENERGY_STAR_Score", sorter: true },
        { title: "Commercial Cost of Electricity", dataIndex: "ELECTRICITY", key: "ELECTRICITY", sortColumn: "electricity_price", sorter: true },
    ];




    const handleDownloadCSV = async () => {
        const tobeDownload = 5000;
        if (totalCount > tobeDownload && hasDownloaded) {
            toast.warning(`Only ${tobeDownload} records can be downloaded.Please refine your search criteria`);
            return;
        }

        try {
            setHasDownloaded(false);
            const params = {
                offset: 0,
                limit: tobeDownload,

            };

            if (bldType) params.buildingType = bldType;
            if (bldOwner) params.sic_code = bldOwner;
            if (bldMasterId) params.master_id = bldMasterId;
            if (state) params.state = state;
            if (city) params.city = city;
            if (bldScore) params.score = bldScore;
            if (floorArea) params.floor_area_min = floorArea;
            if (EUI) params.site_eui_min = EUI;

            // Inject sorting params dynamically
            if (sortColumn) params.sort_column = sortColumn;
            if (sortOrder) params.sort_order = sortOrder === "ascend" ? "asc" : "desc";

            const response = await apiClient.get(`${apiUrl}/building_list/export`, {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'buildingdata.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success('Download success!', {
                position: 'top-right',
                duration: 5, // Duration in seconds
            });;


        } catch (error) {
            console.error('Failed to download CSV:', error);
            toast.error('Download failed. Please try again.');
        } finally {
            setHasDownloaded(true);
        }
    };



    useImperativeHandle(ref, () => ({
        resetSort: () => {
            setSortColumn(null);
            setSortOrder(null);
            setTableKey(prev => prev + 1);
        }
    }));



    useEffect(() => {
        // Whenever a filter changes, reset the current page to 1
        setCurrentPage(1);
    }, [bldType, bldOwner, bldMasterId, bldScore, state, city, floorArea, EUI]);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData$ = of({
            offset: (currentPage - 1) * pageSize,
            limit: pageSize,
            ...(sortColumn && { sort_column: sortColumn }),
            ...(sortOrder && { sort_order: sortOrder === "ascend" ? "asc" : "desc" }),
            ...(bldType && { buildingType: bldType }),
            ...(bldOwner && { ownerofrecord: bldOwner }),
            ...(bldMasterId && { master_id: bldMasterId }),
            ...(state && { state }),
            ...(city && { city }),
            ...(bldScore?.length > 0 && { score: JSON.stringify(bldScore) }),
            ...(floorArea != null && floorArea !== '' && { floor_area_min: floorArea }),
            ...(EUI != null && EUI !== '' && { site_eui_min: EUI }),
        }).pipe(
            debounceTime(500),
            switchMap((params) =>
                from(apiClient.get(`${apiUrl}/building_list_table`, { params, signal })).pipe(
                    catchError((error) => {
                        console.error('Error fetching facility data:', error);
                        return of(null);
                    })
                )
            )
        );

        const subscription = fetchData$.subscribe((response) => {
            if (response?.data?.building_list) {
                const formattedData = response.data.building_list.map((item, index) => ({
                    key: (currentPage - 1) * pageSize + index + 1,
                    BUILDING_OWNER: item?.ownerofrecord || 'N/A',
                    BUILDING_NAME: item?.Master_Building_Name || 'N/A',
                    BUILDING_TYPE: item?.Master_Building_Type || 'N/A',
                    AREA: item?.floor_area || 'N/A',
                    STATE_NAME: item?.State_Name || 'N/A',
                    CITY: item?.city || 'N/A',
                    YEAR: item?.year_built || 'N/A',
                    ENERGY: item?.ENERGY_STAR_Score != null ? Number(item.ENERGY_STAR_Score) : 'N/A',
                    ELECTRICITY: item?.electricity_price || 'N/A',
                    EUI: item?.Site_EUI_kBtu_per_sqft || 'N/A',
                    fullObject: item,
                }));
                setBuildingData(formattedData);
                setTotalCount(response.data.total_count || 1000);
            }
            setLoading(false);
        });

        setLoading(true);

        return () => {
            controller.abort(); // Cancel fetch
            subscription.unsubscribe(); // Cancel Observable
        };
    }, [currentPage, bldType, bldOwner, bldMasterId, bldScore, state, city, floorArea, EUI, sortColumn, sortOrder]);


    const handleTableChange = (pagination, filters, sorter) => {
        if (sorter.order) {
            const sortedColumn = buildingColumns.find(col => col.dataIndex === sorter.field);
            setSortColumn(sortedColumn?.sortColumn || sorter.field);
            setSortOrder(sorter.order);
        } else {
            setSortColumn(null);
            setSortOrder(null);
        }
    };

    //Opportunity table columns
    const opportunityColumns = [
        {
            title: (
                <div>
                    <div className="table-heading">Account Name</div>
                    <Input
                        size="small"
                        placeholder="Account Name"
                        onChange={(e) => handleOppertunitySearch(e.target.value, 'accountName')}
                        prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                    />
                </div>
            ),

            dataIndex: "account_name",
            key: "account_name",
            sortColumn: "account_name",
            sorter: true,
            fixed: 'left',
        },
        { title: "Account ID", dataIndex: "account_id", key: "account_id", sortColumn: "account_id", sorter: true, fixed: 'left', },
        {
            title: (
                <div>
                    <div className="table-heading">Opportunity Name</div>
                    <Input
                        size="small"
                        placeholder="Opportunity Name"
                        onChange={(e) => handleOppertunitySearch(e.target.value, 'opportunityName')}
                        prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                    />
                </div>
            ),
            dataIndex: "opportunity_name",
            key: "opportunity_name",
            sortColumn: "opportunity_name", sorter: true, fixed: 'left',
        },


        {
            title: (
                <div>
                    <div className="table-heading">Description</div>
                    <Input
                        size="small"
                        placeholder="Description"
                        onChange={(e) => handleOppertunitySearch(e.target.value, 'description')}
                        prefix={<SearchOutlined style={{ color: "#aaa" }} />}

                    />
                </div>
            ),
            dataIndex: "description",
            key: "description",
            sortColumn: "description", sorter: true,
            render: (text) => (
                <Tooltip
                    title={
                        <div
                            style={{
                                maxHeight: 300,
                                overflowY: "auto",
                                whiteSpace: "normal",
                            }}
                        >
                            {text}
                        </div>
                    }
                >
                    <div
                        style={{
                            maxWidth: 300,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                        }}
                    >
                        {text}
                    </div>
                </Tooltip>
            ),
        },
        { title: "Opportunity Team Member", dataIndex: "opportunity_team_member", key: "opportunity_team_member", sortColumn: "opportunity_team_member", sorter: true },
        {
            title: (
                <div>
                    <div className="table-heading">Main Contact</div>
                    <Input
                        size="small"
                        placeholder="Main Contact"
                        onChange={(e) => handleOppertunitySearch(e.target.value, 'contactName')}
                        prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                    />
                </div>
            ),
            dataIndex: "main_contact",
            key: "main_contact",
            sortColumn: "main_contact", sorter: true
        },
        { title: "Business Unit", dataIndex: "business_unit", key: "business_unit", sortColumn: "business_unit", sorter: true },
        { title: "Region Branch", dataIndex: "region_branch", key: "region_branch", sortColumn: "region_branch", sorter: true },
        { title: "Business Type", dataIndex: "business_type", key: "business_type", sortColumn: "business_type", sorter: true },
        { title: "Total Amount", dataIndex: "total_amount", key: "total_amount", sortColumn: "total_amount", sorter: true },
        { title: "Stage", dataIndex: "stage", key: "stage", sortColumn: "stage", sorter: true },
        { title: "Profitability(%)", dataIndex: "profitability", key: "profitability", sortColumn: "profitability", sorter: true },
        { title: "Close Date", dataIndex: "close_date", key: "close_date", sortColumn: "close_date", sorter: true },
        { title: "Start Date", dataIndex: "start_date", key: "start_date", sortColumn: "start_date", sorter: true },
        { title: "Comment", dataIndex: "competitor_comment", key: "competitor_comment", sortColumn: "competitor_comment", sorter: true },
        { title: "Age(days)", dataIndex: "age", key: "age", sortColumn: "age", sorter: true },
    ];


    useEffect(() => {
        const subscription = searchSubjectRef.current
            .pipe(debounceTime(300)) // wait 300ms after last input
            .subscribe(({ value, type }) => {
                switch (type) {
                    case 'accountName':
                        setAcccountName(value);
                        break;
                    case 'opportunityName':
                        setOppName(value);
                        break;
                    case 'description':
                        setOppDesc(value);
                        break;
                    case 'contactName':
                        setContactName(value);
                        break;
                    default:
                        break;
                }
            });

        return () => subscription.unsubscribe();
    }, [acccountName, oppName, oppDesc, contactName]);

    const handleOppertunitySearch = (value, type) => {
        searchSubjectRef.current.next({ value, type });
    };

    useEffect(() => {
        const observable$ = from([null]).pipe(
            tap(() => setOpportunityLoading(true)),

            switchMap(() => {
                const params = {
                    offset: (opportunityPage - 1) * opportunityPageSize,
                    limit: opportunityPageSize,
                };

                if (state) params.state = state;
                if (city) params.city = city;
                if (acccountName) params.account_name = acccountName;
                if (oppName) params.opportunity_name = oppName;
                if (oppDesc) params.opp_description = oppDesc;
                if (contactName) params.contact_name = contactName;
                if (opsortColumn) params.sort_column = opsortColumn;
                if (opsortOrder) {
                    params.sort_order = opsortOrder === "ascend" ? "asc" : "desc";
                }
                return from(apiClient.get(`${apiUrl}/opportunity_list_table`, { params }));
            }),

            switchMap((response) => {
                const list = response.data?.opportunity_list || [];

                const formattedOpportunities = list.map((item, index) => ({
                    key: (opportunityPage - 1) * opportunityPageSize + index + 1,
                    account_name: item.ACCOUNTLONGNAME__C || 'N/A',
                    account_id: item.ACCOUNT_ID || 'N/A',
                    opportunity_name: item.NAME || 'N/A',
                    description: item.DESCRIPTION || 'N/A',
                    opportunity_team_member: item.OPPORTUNITY_TEAM_MEMBERS__C || 'N/A',
                    main_contact: item.CONTACT_NAME || 'N/A',
                    business_unit: item.BUSINESS_UNIT__C || 'N/A',
                    region_branch: item.REGION_BRANCH__C || 'N/A',
                    business_type: item.BUSINESS_TYPE__C || 'N/A',
                    total_amount: item.AMOUNT || 'N/A',
                    stage: item.STAGE_NAME || 'N/A',
                    profitability: item.PROBABILITY || 'N/A',
                    close_date: item.CLOSE_DATE?.split('T')[0] || 'N/A',
                    start_date: item.PROJECT_START_DATE__C?.split('T')[0] || 'N/A',
                    competitor_comment: item.COMPETITOR_COMMENT__C || 'N/A',
                    age: item.AGE__C || 'N/A',
                }));

                setOpportunityData(formattedOpportunities);
                setOpportunityTotalCount(response.data.total_count || 1000);
                return of(null); // continue the stream
            }),

            catchError((err) => {
                console.error('Error fetching opportunity data:', err);
                return of(null); // recover gracefully
            }),

            tap(() => setOpportunityLoading(false))
        );

        const subscription = observable$.subscribe();

        return () => {
            // Cleanup on component unmount or dependency change
            subscription.unsubscribe();
        };
    }, [opportunityPage, state, city, opportunityPageSize, acccountName, oppName, oppDesc, contactName, opsortColumn, opsortOrder]);

    const handleTableChangeOp = (pagination, filters, sorter) => {
        if (sorter.order) {
            const sortedColumn = opportunityColumns.find(col => col.dataIndex === sorter.field);
            setSortColumnOp(sortedColumn?.sortColumn || sorter.field);
            setSortOrderOp(sorter.order);
        } else {
            setSortColumnOp(null);
            setSortOrderOp(null);
        }
    };

    const handleOpportunityDownload = async () => {
        const tobeDownload = 5000;
        if (opportunityTotalCount > tobeDownload && hasDownloaded) {
            toast.warning(`Only ${tobeDownload} records can be downloaded. Please refine your search criteria`);
            return;
        }

        try {
            setHasDownloaded(false);
            const params = {
                offset: 0,
                limit: tobeDownload,
            };

            // Add opportunity-specific filters here if any (e.g., status, opportunity type, etc.)
            if (state) params.state = state;
            if (city) params.city = city;
            if (acccountName) params.account_name = acccountName;
            if (oppName) params.opportunity_name = oppName;
            if (oppDesc) params.opp_description = oppDesc;
            if (contactName) params.contact_name = contactName;


            // Inject sorting params dynamically
            if (opsortColumn) params.sort_column = opsortColumn;
            if (opsortOrder) params.sort_order = opsortOrder === "ascend" ? "asc" : "desc";
            const response = await apiClient.get(`${apiUrl}/opportunity_list_table/export`, {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'opportunity_data.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success('Download success!', {
                position: 'top-right',
                duration: 5,
            });

        } catch (error) {
            console.error('Failed to download CSV:', error);
            toast.error('Download failed. Please try again.');
        } finally {
            setHasDownloaded(true);
        }
    };

    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="Building" key="1">

                <Popover
                    content="Max 5000 records can be downloaded"
                    placement="top"
                    trigger="hover"
                >
                    <DownloadOutlined
                        style={{
                            fontSize: 20,
                            color: !hasDownloaded ? 'gray' : '#1890ff',
                            cursor: !hasDownloaded ? 'not-allowed' : 'pointer',
                            float: 'right',
                            marginRight: '5px',
                        }}
                        onClick={handleDownloadCSV}
                    />
                </Popover>


                <Table
                    key={tableKey}
                    columns={buildingColumns}
                    dataSource={buildingData}
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        total: totalCount,
                        pageSize: pageSize,
                        onChange: (page) => setCurrentPage(page), // Update page on change
                    }}
                    scroll={{
                        x: "max-content",
                        y: 300,
                    }}
                    rowClassName={(record) =>
                        record.fullObject.master_id === bldMasterId
                            ? 'highlighted-row'
                            : ''
                    }
                    onChange={handleTableChange}
                />
            </TabPane>
            <TabPane tab="Opportunities" key="2">

                <Popover
                    content="Max 5000 records can be downloaded"
                    placement="top"
                    trigger="hover"
                >
                    <DownloadOutlined
                        style={{
                            fontSize: 20,
                            color: !hasDownloaded ? 'gray' : '#1890ff',
                            cursor: !hasDownloaded ? 'not-allowed' : 'pointer',
                            float: 'right',
                            marginRight: '5px',
                        }}
                        onClick={handleOpportunityDownload}
                    />
                </Popover>
                <Table
                    columns={opportunityColumns}
                    dataSource={opportunityData}
                    loading={opportunityLoading}
                    pagination={
                        opportunityData.length > 0
                            ? {
                                current: opportunityPage,
                                total: opportunityTotalCount,
                                pageSize: opportunityPageSize,
                                onChange: (page) => setOpportunityPage(page),
                            }
                            : false
                    }
                    scroll={{
                        x: "max-content",
                        y: 300,
                    }}
                    onChange={handleTableChangeOp}
                />
            </TabPane>
        </Tabs>
    );
});

export default BuildingTable;
